import {
    ReactNode,
    PropsWithChildren,
    useRef,
    useLayoutEffect,
    useCallback,
    useEffect,
    useContext,
    ForwardedRef,
    ReactElement,
    isValidElement,
} from 'react';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { CustomScrollContext } from '@hh.ru/magritte-internal-custom-scroll';
import { FcWithFlag } from '@hh.ru/magritte-types';

interface TreeSelectorWrapperProps extends PropsWithChildren {
    forwardRef: ForwardedRef<HTMLDivElement>;
}

const toNumber = (value: string) => {
    const result = parseFloat(value);
    return !Number.isNaN(result) ? result : 0;
};

export const TreeSelectorWrapper: FcWithFlag<TreeSelectorWrapperProps, 'isTreeSelectorWrapper'> = ({
    forwardRef,
    ...props
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const wrapperMultiRef = useMultipleRefs(wrapperRef, forwardRef);
    const scrollContext = useContext(CustomScrollContext);

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const calcHeight = useCallback(
        throttle(() => {
            if (!wrapperRef.current?.parentElement) {
                return;
            }
            const parentHeight = wrapperRef.current.parentElement.getBoundingClientRect().height;
            const style = window.getComputedStyle(wrapperRef.current.parentElement) || {};
            const paddingTop = toNumber(style.paddingTop || '0');
            const paddingBottom = toNumber(style.paddingBottom || '0');
            const borderTop = toNumber(style.borderTop || '0');
            const borderBottom = toNumber(style.borderBottom || '0');
            // срабатывает до старта анимации модала, поэтому можно использовать getBoundingClientRect
            const height = parentHeight - paddingTop - paddingBottom - borderTop - borderBottom;
            wrapperRef.current.style.height = `${height}px`;
        }, 50),
        []
    );

    useLayoutEffect(() => {
        if (!wrapperRef.current?.parentElement) {
            return void 0;
        }
        const parentObserver = new ResizeObserver(calcHeight);
        parentObserver.observe(wrapperRef.current.parentElement);
        return () => parentObserver.disconnect();
    }, [calcHeight]);

    useEffect(() => {
        const wrapperElement = wrapperRef.current;
        if (!wrapperElement || !scrollContext) {
            return void 0;
        }

        const handleTouchMove = (event: TouchEvent) => {
            event.stopPropagation();
        };

        wrapperElement.addEventListener('touchmove', handleTouchMove);
        return () => wrapperElement.removeEventListener('touchmove', handleTouchMove);
    }, [scrollContext]);

    return <div style={{ overflow: 'auto' }} ref={wrapperMultiRef} data-qa="tree-selector-container" {...props} />;
};

TreeSelectorWrapper.isTreeSelectorWrapper = true;

export const isValidTreeSelectorWrapper = (
    component: ReactNode
): component is ReactElement<TreeSelectorWrapperProps> => {
    return (
        isValidElement(component) && typeof component.type === 'function' && 'isTreeSelectorWrapper' in component.type
    );
};
